import { useState, type ReactNode } from "react";
import { regions, years, type Region, type SortKey } from "@/utils/media";

export interface CategoryFilters {
  region: Region | "all";
  year: number | "all";
  sort: SortKey;
}

interface FilterBarProps {
  filters: CategoryFilters;
  onChange: (next: Partial<CategoryFilters>) => void;
}

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "updated", label: "最近更新" },
  { key: "popularity", label: "热度最高" },
  { key: "rating", label: "评分最高" },
  { key: "year", label: "年份最新" },
];

interface GroupProps {
  label: string;
  children: ReactNode;
}

function FilterGroup({ label, children }: GroupProps) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
      <span className="shrink-0 text-sm font-medium text-foreground-600 sm:w-16">
        {label}
      </span>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

interface PillProps {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}

function Pill({ active, onClick, children }: PillProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition-colors ${
        active
          ? "bg-primary-500 font-medium text-background-50"
          : "bg-background-100 text-foreground-700 hover:bg-background-200"
      }`}
    >
      {children}
    </button>
  );
}

export default function FilterBar({ filters, onChange }: FilterBarProps) {
  const [open, setOpen] = useState(false);

  const activeCount =
    (filters.region !== "all" ? 1 : 0) +
    (filters.year !== "all" ? 1 : 0) +
    (filters.sort !== "updated" ? 1 : 0);

  return (
    <div className="rounded-lg border border-background-200 bg-background-50">
      <div className="flex items-center justify-between gap-3 border-b border-background-200 px-4 py-3 md:px-5 lg:hidden">
        <span className="flex items-center gap-2 text-sm font-medium text-foreground-800">
          <i className="ri-equalizer-line text-primary-500"></i>
          筛选条件
          {activeCount > 0 && (
            <span className="rounded-full bg-accent-500 px-1.5 text-xs text-background-50">
              {activeCount}
            </span>
          )}
        </span>
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-700"
        >
          {open ? "收起" : "展开"}
          <i className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
        </button>
      </div>

      <div
        className={`${open ? "block" : "hidden"} space-y-4 p-4 md:p-5 lg:block`}
      >
        <FilterGroup label="地区">
          <Pill active={filters.region === "all"} onClick={() => onChange({ region: "all" })}>
            全部
          </Pill>
          {regions.map((region) => (
            <Pill
              key={region}
              active={filters.region === region}
              onClick={() => onChange({ region })}
            >
              {region}
            </Pill>
          ))}
        </FilterGroup>

        <FilterGroup label="年份">
          <Pill active={filters.year === "all"} onClick={() => onChange({ year: "all" })}>
            全部
          </Pill>
          {years.map((year) => (
            <Pill
              key={year}
              active={filters.year === year}
              onClick={() => onChange({ year })}
            >
              {year}
            </Pill>
          ))}
        </FilterGroup>

        <FilterGroup label="排序">
          {sortOptions.map((option) => (
            <Pill
              key={option.key}
              active={filters.sort === option.key}
              onClick={() => onChange({ sort: option.key })}
            >
              {option.label}
            </Pill>
          ))}
        </FilterGroup>
      </div>
    </div>
  );
}